import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { User, Car, Wrench, DollarSign, Lock } from "lucide-react";
import { api, errMsg, toLocalInput, fromLocalInput } from "../../lib/api";
import { Btn, Field, Input, Textarea, Select, Section } from "../../components/kit";
import PricingBox from "../../components/PricingBox";

const EMPTY = {
  customer: { name: "", phone: "", address: "", city: "", state: "OH", zip: "" },
  vehicle: { year: "", make: "", model: "", engine: "" },
  service_key: "", description: "", required_skills: "", required_tools: "", window_start: "", window_end: "",
  parts_responsibility: "driven", parts_ready: "", admin_notes: "", is_demo: false, intake_photo_ids: [],
  pricing: { subtotal: "", parts_cost: "", payout: "", other_costs: "", tax_rate: "", tax_confirmed: false },
};

export default function JobForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [services, setServices] = useState([]);
  const [status, setStatus] = useState("draft");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
      const [s, st] = await Promise.all([api.get("/services"), api.get("/admin/settings")]);
      setServices(s.data);
      if (id) {
        const { data } = await api.get(`/jobs/${id}`);
        setStatus(data.status);
        setF({ ...EMPTY, ...data, window_start: toLocalInput(data.window_start), window_end: toLocalInput(data.window_end) });
      } else setF({ ...EMPTY, pricing: { ...EMPTY.pricing, tax_rate: st.data.tax_rate } });
      } catch (e) {
        toast.error(errMsg(e));
        nav("/a/jobs");
      }
    })();
  }, [id, nav]);
  if (!f) return <div className="h-40 animate-pulse bg-[#141414] rounded-2xl" />;
  const frozen = status !== "draft";
  const set = (path) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const [a, b] = path.split(".");
    setF(b ? { ...f, [a]: { ...f[a], [b]: v } } : { ...f, [a]: v });
  };
  const pickService = (e) => {
    const s = services.find((x) => x.key === e.target.value);
    const p = { ...f.pricing };
    if (s && !frozen) {
      if (!Number(p.subtotal) && s.default_price) p.subtotal = s.default_price;
      if (!Number(p.payout) && s.default_payout) p.payout = s.default_payout;
    }
    setF({ ...f, service_key: e.target.value, pricing: p, required_tools: f.required_tools || (s?.requires_equipment ? s.equipment_note : "") });
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const num = (v) => Number(v || 0);
    const body = {
      ...f, window_start: fromLocalInput(f.window_start), window_end: fromLocalInput(f.window_end),
      pricing: { ...f.pricing, subtotal: num(f.pricing.subtotal), parts_cost: num(f.pricing.parts_cost), payout: num(f.pricing.payout),
        other_costs: num(f.pricing.other_costs), tax_rate: num(f.pricing.tax_rate) },
    };
    try {
      const { data } = id ? await api.put(`/admin/jobs/${id}`, body) : await api.post("/admin/jobs", body);
      toast.success(id ? "Saved" : "Draft created");
      nav(`/a/jobs/${data.id}`);
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const svc = services.find((x) => x.key === f.service_key);
  return (
    <form onSubmit={submit} className="space-y-4 fade-in" data-testid="job-form">
      <h1 className="font-head text-3xl uppercase font-bold">{id ? `Edit #${f.number}` : "New job"}</h1>
      <Section title="Customer" icon={User}>
        <Field label="Name"><Input value={f.customer.name} onChange={set("customer.name")} required data-testid="job-customer-name" /></Field>
        <Field label="Phone"><Input type="tel" value={f.customer.phone} onChange={set("customer.phone")} data-testid="job-customer-phone" /></Field>
        <Field label="Street address"><Input value={f.customer.address} onChange={set("customer.address")} data-testid="job-customer-address" /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="City"><Input value={f.customer.city} onChange={set("customer.city")} data-testid="job-customer-city" /></Field>
          <Field label="State"><Input value={f.customer.state || ""} maxLength={2} onChange={set("customer.state")} data-testid="job-customer-state" /></Field>
          <Field label="ZIP"><Input inputMode="numeric" value={f.customer.zip} onChange={set("customer.zip")} required data-testid="job-customer-zip" /></Field>
        </div>
      </Section>
      <Section title="Vehicle" icon={Car}>
        <div className="grid grid-cols-2 gap-3">
          {["year", "make", "model", "engine"].map((k) => (
            <Field key={k} label={k}><Input value={f.vehicle[k]} onChange={set(`vehicle.${k}`)} data-testid={`job-vehicle-${k}`} /></Field>
          ))}
        </div>
      </Section>
      <Section title="Service" icon={Wrench}>
        <Field label="Service" hint={svc?.requires_equipment ? "Equipment-dependent: only specialists approved for this service get offers." : ""}>
          <Select value={f.service_key} onChange={pickService} required disabled={frozen && !!f.assigned_to} data-testid="job-service-select">
            <option value="">Choose…</option>
            {services.filter((s) => s.active).map((s) => <option key={s.key} value={s.key}>{s.name} ({s.category})</option>)}
          </Select>
        </Field>
        <Field label="Problem / scope description"><Textarea value={f.description} onChange={set("description")} data-testid="job-description" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Required skills"><Input value={f.required_skills} onChange={set("required_skills")} data-testid="job-required-skills" /></Field>
          <Field label="Required tools"><Input value={f.required_tools} onChange={set("required_tools")} data-testid="job-required-tools" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Window start"><Input type="datetime-local" value={f.window_start} onChange={set("window_start")} required data-testid="job-window-start" /></Field>
          <Field label="Window end"><Input type="datetime-local" value={f.window_end} onChange={set("window_end")} required data-testid="job-window-end" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Parts">
            <Select value={f.parts_responsibility} onChange={set("parts_responsibility")} data-testid="job-parts-responsibility">
              <option value="driven">Driven-supplied</option><option value="customer">Customer-supplied (approved)</option><option value="none">No parts</option>
            </Select>
          </Field>
          <Field label="Parts readiness"><Input value={f.parts_ready} onChange={set("parts_ready")} placeholder="e.g. picked up, on site" data-testid="job-parts-ready" /></Field>
        </div>
      </Section>
      <Section title="Pricing" icon={DollarSign} right={frozen && <span className="text-xs text-neutral-500 flex items-center gap-1"><Lock className="w-3 h-3" />Use Revise quote</span>}>
        <div className="grid grid-cols-2 gap-3">
          {[["subtotal", "Customer subtotal"], ["parts_cost", "Actual parts/supplies"], ["payout", "Specialist payout"], ["other_costs", "Other direct costs"], ["tax_rate", "Tax rate %"]].map(([k, l]) => (
            <Field key={k} label={l}><Input type="number" step="0.01" min="0" disabled={frozen} value={f.pricing[k]} onChange={set(`pricing.${k}`)} data-testid={`job-pricing-${k}`} /></Field>
          ))}
        </div>
        <label className="flex gap-2 text-sm items-start">
          <input type="checkbox" disabled={frozen} checked={f.pricing.tax_confirmed} onChange={set("pricing.tax_confirmed")} className="mt-1 accent-[#FF6B00]" data-testid="job-tax-confirmed" />
          Tax rate confirmed for this service address
        </label>
        <PricingBox p={f.pricing} />
      </Section>
      <Section title="Private admin notes">
        <Textarea value={f.admin_notes} onChange={set("admin_notes")} placeholder="Never shown to specialists" data-testid="job-admin-notes" />
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.is_demo} onChange={set("is_demo")} className="accent-[#FF6B00]" data-testid="job-is-demo" />Demo / test job (excluded from real exports)</label>
      </Section>
      <p className="text-xs text-neutral-500">Photos can be added on the job page after saving.</p>
      <Btn size="lg" className="w-full" loading={busy} data-testid="job-save-btn">{id ? "Save changes" : "Save draft"}</Btn>
    </form>
  );
}
